import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../render/layout';
import { makeTwoPassText } from '../render/twoPassText';
import { pickIdleAnimKey } from '../render/puppyAnimations';
import { puppyForId } from '../game/Puppy';
import { PUPPY_ANCHORS, PUPPY_MAX_DIM } from '../render/puppyAnchors.generated';
import { alignmentForStill, alignmentForAnim } from '../render/puppyAlignmentOffsets';
import type { PuppyId } from '../game/types';

const ANIM_INTERVAL_MS = 4000;
const DOUG_SIZE = 280;
const OK_FADE_DELAY_MS = 0;
const OK_FADE_DURATION_MS = 7000;

interface WelcomeInit {
  onComplete?: () => void;
}

// First-launch onboarding page: dark-green backdrop, two-pass title,
// Doug (puppy 1) idling like the Big Puppy panel, two body lines, and an
// OK! button that fades in after ~15 s.
export class WelcomeScene extends Phaser.Scene {
  private onComplete: (() => void) | null = null;
  private dougSprite!: Phaser.GameObjects.Sprite;
  private dougBaseX = 0;
  private dougBaseY = 0;
  private dougPuppyId: PuppyId = 'doug';
  // null = still mode; integer = currently playing animation (0-based).
  private currentAnimIdx: number | null = null;
  private animTimer?: Phaser.Time.TimerEvent;
  private okFadeTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super({ key: 'Welcome' });
  }

  init(data?: WelcomeInit): void {
    this.onComplete = data?.onComplete ?? null;
  }

  create(): void {
    this.add
      .rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x1f4711)
      .setOrigin(0, 0)
      .setDepth(1000);

    makeTwoPassText(this, LOGICAL_WIDTH / 2, 120, 'Welcome to Puppies!', {
      fontSize: '72px',
      depth: 1010,
    });

    const doug = puppyForId('doug');
    const dougY = 350;
    const stillAtlas = `p${doug.puppyNum}a1`;
    this.dougBaseX = LOGICAL_WIDTH / 2;
    this.dougBaseY = dougY;
    this.dougPuppyId = doug.id;
    this.dougSprite = this.add
      .sprite(LOGICAL_WIDTH / 2, dougY, stillAtlas, 'frame1')
      .setDepth(1010);
    this.applyFrameAnchor(stillAtlas, 'frame1', doug.puppyNum);

    this.animTimer = this.time.addEvent({
      delay: ANIM_INTERVAL_MS,
      loop: true,
      callback: () => this.playRandomAnim(doug.puppyNum),
    });

    makeTwoPassText(
      this,
      LOGICAL_WIDTH / 2,
      540,
      'Before you start,\nevery puppy needs\na little training.',
      {
        fontSize: '56px',
        originX: 0.5,
        originY: 0,
        depth: 1010,
        wordWrapWidth: LOGICAL_WIDTH - 80,
        align: 'center',
        lineSpacing: 6,
      },
    );

    makeTwoPassText(
      this,
      LOGICAL_WIDTH / 2,
      750,
      'Let us show you\nhow this works\nso that you can help\nthe puppies play\nin all the parks!',
      {
        fontSize: '56px',
        originX: 0.5,
        originY: 0,
        depth: 1010,
        wordWrapWidth: LOGICAL_WIDTH - 80,
        align: 'center',
        lineSpacing: 6,
      },
    );

    this.buildOkButton();
  }

  shutdown(): void {
    this.animTimer?.remove(false);
    this.okFadeTimer?.remove(false);
  }

  private buildOkButton(): void {
    const w = 360;
    const h = 90;
    const cx = LOGICAL_WIDTH / 2;
    const cy = LOGICAL_HEIGHT - 110;

    const container = this.add.container(cx, cy).setDepth(1010).setAlpha(0.15);

    const bg = this.add.graphics();
    bg.fillStyle(0xffeb00, 1);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 30);
    bg.lineStyle(6, 0xcc0000);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 30);
    container.add(bg);

    const text = makeTwoPassText(this, 0, 0, "Let's Go!", { fontSize: '52px' });
    container.add([text.outline, text.fill]);

    container.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    container.on('pointerdown', () => this.dismiss());
    container.input!.cursor = 'pointer';
    // Button isn't tappable until it's faded in.
    container.disableInteractive();

    this.okFadeTimer = this.time.delayedCall(OK_FADE_DELAY_MS, () => {
      this.tweens.add({
        targets: container,
        alpha: 1,
        duration: OK_FADE_DURATION_MS,
        onComplete: () => {
          container.setInteractive(
            new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
            Phaser.Geom.Rectangle.Contains,
          );
        },
      });
    });
  }

  // Mirrors ColorPickerView's nose-anchor logic so Doug stays pinned across
  // still ↔ animation frames. Adds the per-(puppy, anim) tuning offset.
  private applyFrameAnchor(atlasKey: string, frameName: string, puppyNum: number): void {
    const a = PUPPY_ANCHORS[atlasKey]?.[frameName] ?? { x: 0.5, y: 0.5 };
    const maxDim = PUPPY_MAX_DIM[String(puppyNum)] ?? 200;
    const scale = DOUG_SIZE / maxDim;
    this.dougSprite.setScale(scale);
    this.dougSprite.setOrigin(a.x, a.y);
    const offset = this.currentAnimIdx === null
      ? alignmentForStill(this.dougPuppyId, DOUG_SIZE)
      : alignmentForAnim(this.dougPuppyId, this.currentAnimIdx, DOUG_SIZE);
    this.dougSprite.setPosition(this.dougBaseX + offset.dx, this.dougBaseY + offset.dy);
  }

  private playRandomAnim(puppyNum: number): void {
    if (!this.dougSprite || this.dougSprite.anims.isPlaying) return;
    const animKey = pickIdleAnimKey(puppyNum, (max) => Math.floor(Math.random() * max));
    const atlasKey = animKey.replace(/^puppy(\d+)\.anim(\d+)$/, 'p$1a$2');
    if (!this.anims.exists(animKey)) return;
    const animNum = Number(animKey.match(/anim(\d+)$/)?.[1] ?? 1);
    this.currentAnimIdx = animNum - 1;

    this.dougSprite.off('animationupdate');
    this.dougSprite.on(
      'animationupdate',
      (_anim: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
        this.applyFrameAnchor(atlasKey, frame.textureFrame as string, puppyNum);
      },
    );

    this.dougSprite.play({ key: animKey, repeat: 0 });
    const firstFrame = this.anims.get(animKey)?.frames[0]?.textureFrame as string | undefined;
    if (firstFrame) this.applyFrameAnchor(atlasKey, firstFrame, puppyNum);

    this.dougSprite.once('animationcomplete', () => {
      this.dougSprite.off('animationupdate');
      this.currentAnimIdx = null;
      const stillAtlas = `p${puppyNum}a1`;
      this.dougSprite.setTexture(stillAtlas, 'frame1');
      this.applyFrameAnchor(stillAtlas, 'frame1', puppyNum);
    });
  }

  private dismiss(): void {
    this.onComplete?.();
    this.scene.stop();
  }
}
